import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { CreditCard, ArrowLeft, AlertCircle, CheckCircle, XCircle, Clock, Download, Receipt, ChevronDown, Sparkles } from 'lucide-react';

interface SubscriptionInfo {
  plan: string;
  billing_cycle: string;
  status: string;
  next_billing_date: string;
  amount: string;
  cancel_at_period_end: boolean;
}

interface ProfileData {
  subscription_type: string;
  subscription_period_end: number | null;
}

interface PaymentMethod {
  type: string;
  card?: {
    type: string;
    last4: string;
    expiry_month: number;
    expiry_year: number;
  };
}

interface Transaction {
  id: string;
  status: string;
  created_at: number;
  details: {
    totals: {
      total: string;
      currency_code: string;
    };
  };
  items: Array<{
    price: {
      description: string;
    };
  }>;
}

const BillingSettings: React.FC = () => {
  const { t, i18n } = useTranslation(['profile', 'common']);
  const navigate = useNavigate();
  const { isSignedIn, apiCall, user, isLoading: authLoading } = useAuth();
  const [dataLoading, setDataLoading] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [billingHistory, setBillingHistory] = useState<Transaction[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [actionModal, setActionModal] = useState<{ type: 'cancel_success' | 'reactivate_success' | 'error'; message?: string } | null>(null);
  const [confirmModal, setConfirmModal] = useState<'cancel' | 'reactivate' | null>(null);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  useEffect(() => {
    // Auto-scroll to top when page loads
    window.scrollTo(0, 0);

    // Wait for auth to finish loading before redirecting
    if (authLoading) {
      return; // Still loading auth state
    }

    if (!isSignedIn) {
      navigate('/');
      return;
    }

    if (user?.userId) {
      fetchBillingData();
    }
  }, [isSignedIn, user?.userId, authLoading]);

  const fetchBillingData = async () => {
    if (!user?.userId) {
      console.error('No user ID available');
      setDataLoading(false);
      return;
    }

    try {
      setDataLoading(true);

      // Fetch profile data (optional - fallback to subscription data if fails)
      try {
        const profileResponse = await apiCall(`${apiBaseUrl}/api/v1/profiles/${user.userId}`);
        if (profileResponse.ok) {
          const profile = await profileResponse.json();
          setProfileData({
            subscription_type: profile.subscription_type,
            subscription_period_end: profile.subscription_period_end
          });
        } else {
          console.warn('⚠️ Profile fetch failed (403), will use subscription data only:', profileResponse.status);
        }
      } catch (err) {
        console.warn('⚠️ Profile fetch error, will use subscription data only:', err);
      }

      // Fetch subscription details from Payment Service (use user ID)
      console.debug('🔍 Fetching subscription for user:', user.userId);
      const subResponse = await apiCall(`${apiBaseUrl}/api/v1/payment/subscription/${user.userId}`);
      console.debug('📡 Subscription API response:', subResponse.status, subResponse.ok);
      
      if (subResponse.ok) {
        const subData = await subResponse.json();
        console.debug('✅ Subscription data:', subData);
        setSubscriptionInfo(subData);
        
        // Fetch payment method for premium users
        if (subData.status === 'active') {
          fetchPaymentMethod();
        }
        
        // Fetch billing history for premium and canceled users (past invoices)
        if (subData.status === 'active' || subData.status === 'canceled') {
          fetchBillingHistory();
        }
      } else {
        const errorText = await subResponse.text();
        console.error('❌ Subscription fetch failed:', subResponse.status, errorText);
      }
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const fetchPaymentMethod = async () => {
    try {
      const response = await apiCall(`${apiBaseUrl}/api/v1/payment/payment-method`);
      if (response.ok) {
        const data = await response.json();
        setPaymentMethod(data.payment_method);
      }
    } catch (error) {
      console.error('Failed to fetch payment method:', error);
    }
  };

  const fetchBillingHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await apiCall(`${apiBaseUrl}/api/v1/payment/billing-history`);
      if (response.ok) {
        const data = await response.json();
        setBillingHistory(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch billing history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDownloadInvoice = async (transactionId: string) => {
    try {
      const response = await apiCall(`${apiBaseUrl}/api/v1/payment/invoice/${transactionId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.url) {
          window.open(data.url, '_blank');
        }
      }
    } catch (error) {
      console.error('Failed to download invoice:', error);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(t('billing.cancelConfirm'))) return;

    try {
      setActionLoading(true);
      const response = await apiCall(`${apiBaseUrl}/api/v1/payment/subscription/cancel`, {
        method: 'POST',
      });

      if (response.ok) {
        await fetchBillingData();
        setActionModal({ type: 'cancel_success' });
      } else {
        const errorData = await response.json();
        setActionModal({ type: 'error', message: errorData.detail || t('billing.cancelError') });
      }
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      setActionModal({ type: 'error', message: t('billing.cancelError') });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setActionLoading(true);
      const response = await apiCall(`${apiBaseUrl}/api/v1/payment/subscription/reactivate`, {
        method: 'POST',
      });

      if (response.ok) {
        await fetchBillingData();
        setActionModal({ type: 'reactivate_success' });
      } else {
        const errorData = await response.json();
        setActionModal({ type: 'error', message: errorData.detail || t('billing.reactivateError') });
      }
    } catch (error) {
      console.error('Failed to reactivate subscription:', error);
      setActionModal({ type: 'error', message: t('billing.reactivateError') });
    } finally {
      setActionLoading(false);
    }
  };

  // Show loading screen while auth or data is loading
  if (authLoading || dataLoading) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-6 sm:py-10">
        <div className="max-w-2xl mx-auto space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 relative overflow-hidden">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-pink-50/80 to-transparent z-10" />
            <div className="space-y-3">
              <div className="h-4 w-1/3 bg-pink-100 rounded-full animate-pulse" />
              <div className="h-3 w-full bg-slate-100 rounded-full animate-pulse" />
              <div className="h-3 w-2/3 bg-slate-100 rounded-full animate-pulse" />
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 relative overflow-hidden">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-pink-50/80 to-transparent z-10" />
            <div className="space-y-3">
              <div className="h-4 w-1/4 bg-slate-200 rounded-full animate-pulse" />
              <div className="h-10 w-full bg-slate-100 rounded-lg animate-pulse" />
              <div className="h-3 w-1/2 bg-slate-100 rounded-full animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return null;
  }

  // Check if user is premium from either profile data OR subscription data
  const isPremium = profileData?.subscription_type?.includes('premium') || 
                    (subscriptionInfo && subscriptionInfo.plan !== 'Free' && subscriptionInfo.status === 'active');

  const parseAmountValue = (amount?: string | null) => {
    if (!amount) return null;
    const normalized = amount.replace(/[^\d.,]/g, '').replace(',', '.');
    const value = Number.parseFloat(normalized);
    return Number.isFinite(value) ? value : null;
  };

  const formatCurrency = (amount: number, currencyCode = 'EUR') =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);

  const latestInvoice = billingHistory[0];
  const latestInvoiceAmount =
    latestInvoice?.details?.totals?.total != null
      ? Number.parseFloat(latestInvoice.details.totals.total) / 100
      : null;
  const latestInvoiceCurrency = latestInvoice?.details?.totals?.currency_code || 'EUR';
  const planAmountValue = parseAmountValue(subscriptionInfo?.amount);
  const hasActivePromoAmount =
    latestInvoiceAmount != null &&
    planAmountValue != null &&
    latestInvoiceAmount < planAmountValue;
  const displayedAmount = hasActivePromoAmount
    ? formatCurrency(latestInvoiceAmount, latestInvoiceCurrency)
    : subscriptionInfo?.amount || 'N/A';

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/profile')}
            className="flex items-center text-slate-600 hover:text-slate-900 mb-3 sm:mb-4 transition-colors text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common:back')}
          </button>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
            <h1 className="text-base sm:text-xl font-bold text-slate-900">{t('billing.title')}</h1>
          </div>
        </div>

        {/* Free Plan - Current Plan Info + Upgrade CTA */}
        {!isPremium && (
          <>
            {/* Current Free Plan */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6 mb-4 sm:mb-6">
              <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-3 sm:mb-4 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                {t('billing.currentPlan')}
              </h2>
              
              <div className="space-y-2 sm:space-y-3 text-sm sm:text-base">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">{t('common:plan')}:</span>
                  <span className="font-bold text-slate-900">
                    {profileData?.subscription_type === 'explorer_free' ? 'Explorer Free' : 
                     profileData?.subscription_type === 'recruiter_free' ? 'Recruiter Free' : 
                     'Free'}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-slate-600">{t('billing.billingCycle')}:</span>
                  <span>-</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-slate-600">{t('billing.amount')}:</span>
                  <span className="font-semibold text-green-600">€0.00 / month</span>
                </div>
              </div>

              <div className="mt-3 sm:mt-4 p-3 bg-slate-100 border border-slate-200 rounded-lg">
                <p className="text-xs sm:text-sm text-slate-700">
                  {t('billing.freePlanNudge')}
                </p>
              </div>
            </div>

            {/* Upgrade CTA - subtle */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-5 mb-4 sm:mb-6 flex items-center justify-between">
              <div>
                <p className="text-sm sm:text-base font-medium text-slate-900">{t('billing.upgradeToPremium')}</p>
                <p className="text-xs sm:text-sm text-slate-500">{t('billing.upgradeNudge')}</p>
              </div>
              <button
                onClick={() => navigate('/pricing')}
                className="text-pink-600 hover:text-pink-700 font-semibold text-sm sm:text-base whitespace-nowrap ml-4 transition-colors"
              >
                {t('billing.viewAllPlans')} →
              </button>
            </div>
          </>
        )}

        {/* Current Subscription */}
        {isPremium && subscriptionInfo && (
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-3 sm:mb-4 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              {t('billing.currentPlan')}
            </h2>
            
            <div className="space-y-2 sm:space-y-3 text-sm sm:text-base">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">{t('common:plan')}:</span>
                <span className="font-bold text-pink-600">
                  {profileData?.subscription_type === 'explorer_premium' ? t('subscription.explorer_premium') : 
                   profileData?.subscription_type === 'recruiter_premium' ? t('subscription.recruiter_premium') : 
                   'Premium'}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-slate-600">{t('billing.billingCycle')}:</span>
                <span className="capitalize">{t(`billing.${subscriptionInfo.billing_cycle || 'monthly'}`)}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-slate-600">{t('billing.amount')}:</span>
                <div className="text-right">
                  {hasActivePromoAmount && (
                    <div className="text-xs text-slate-400 line-through">
                      {subscriptionInfo.amount}
                    </div>
                  )}
                  <div className="font-semibold text-slate-900">{displayedAmount}</div>
                  {hasActivePromoAmount && (
                    <div className="text-xs font-medium text-pink-600">
                      {t('billing.promoActive')}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-slate-600">
                  {subscriptionInfo.cancel_at_period_end ? t('billing.subscriptionEnds') : t('billing.nextBilling')}:
                </span>
                <span>
                  {(subscriptionInfo.next_billing_date && subscriptionInfo.next_billing_date !== '') 
                    ? new Date(subscriptionInfo.next_billing_date).toLocaleDateString(i18n.language)
                    : (profileData?.subscription_period_end 
                        ? new Date(profileData.subscription_period_end * 1000).toLocaleDateString(i18n.language)
                        : 'N/A')}
                </span>
              </div>

              {profileData?.subscription_period_end && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">{t('billing.usageResets')}:</span>
                  <span>{new Date(profileData.subscription_period_end * 1000).toLocaleDateString(i18n.language)}</span>
                </div>
              )}

              {subscriptionInfo.cancel_at_period_end && (
                <div className="mt-3 sm:mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-amber-800">{t('billing.cancelWarning')}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Manage Subscription Actions */}
        {isPremium && subscriptionInfo && (
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-3 sm:mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-pink-500" />
              Manage Subscription
            </h2>
            
            <div className="space-y-2 sm:space-y-3">
              {subscriptionInfo.cancel_at_period_end ? (
                // Reactivate (when canceled)
                <button
                  onClick={() => setConfirmModal('reactivate')}
                  disabled={actionLoading}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:bg-slate-400 text-white font-bold py-2.5 sm:py-3 px-4 sm:px-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  {actionLoading ? (
                    <Clock className="w-5 h-5 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5" />
                  )}
                  {t('billing.reactivateSubscription')}
                </button>
              ) : (
                // Cancel subscription - subtle, small (we don't want to encourage this)
                <button
                  onClick={() => setConfirmModal('cancel')}
                  disabled={actionLoading}
                  className="text-slate-500 hover:text-slate-600 text-xs sm:text-sm py-1.5 px-2 rounded transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  {actionLoading ? (
                    <Clock className="w-3 h-3 animate-spin" />
                  ) : (
                    <XCircle className="w-3 h-3" />
                  )}
                  {t('billing.cancelSubscription')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Payment Method */}
        {isPremium && paymentMethod && (
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-3 sm:mb-4 flex items-center gap-2">
              <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
              {t('billing.paymentMethod')}
            </h2>
            
            <div className="flex items-center justify-between p-3 sm:p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg shadow-sm border border-slate-200">
                  <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-slate-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900 capitalize text-sm sm:text-base">
                    {paymentMethod.card?.type} ending in {paymentMethod.card?.last4}
                  </p>
                  <p className="text-xs sm:text-sm text-slate-600">
                    Expires: {paymentMethod.card?.expiry_month}/{paymentMethod.card?.expiry_year}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Billing History (Collapsible) - also shown for canceled subscriptions */}
        {billingHistory.length > 0 && (
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6 mb-4 sm:mb-6">
            <button
              onClick={() => setHistoryExpanded(!historyExpanded)}
              className="w-full flex items-center justify-between text-left"
            >
              <h2 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
                <Receipt className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                {t('billing.billingHistory')} ({billingHistory.length} {billingHistory.length === 1 ? t('billing.transaction') : t('billing.transactions')})
              </h2>
              <ChevronDown 
                className={`w-4 h-4 sm:w-5 sm:h-5 text-slate-600 transition-transform duration-200 ${historyExpanded ? 'rotate-180' : ''}`}
              />
            </button>
            
            {historyExpanded && (
              <div className="mt-3 sm:mt-4 overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-slate-200">
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm font-semibold text-slate-700">{t('billing.date')}</th>
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm font-semibold text-slate-700">{t('billing.description')}</th>
                      <th className="text-right py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm font-semibold text-slate-700">{t('billing.amount')}</th>
                      <th className="text-right py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm font-semibold text-slate-700">{t('billing.invoice')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingHistory.map((transaction) => (
                      <tr key={transaction.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm text-slate-900">
                          {new Date(transaction.created_at * 1000).toLocaleDateString(i18n.language)}
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm text-slate-900">
                          {transaction.items[0]?.price?.description || 'Subscription Payment'}
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm text-slate-900 text-right font-semibold">
                          {transaction.details.totals.currency_code} {(parseFloat(transaction.details.totals.total) / 100).toFixed(2)}
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-right">
                          <button
                            onClick={() => handleDownloadInvoice(transaction.id)}
                            className="text-pink-600 hover:text-pink-700 flex items-center gap-1 ml-auto transition-colors"
                            title={t('billing.downloadInvoice')}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* View All Plans - Subtle button at bottom */}
        <div className="text-center mt-4 sm:mt-6">
          <button
            onClick={() => navigate('/pricing')}
            className="text-slate-600 hover:text-slate-900 font-medium text-sm underline decoration-dotted underline-offset-4 hover:decoration-solid transition-all"
          >
            {t('billing.viewAllPlans')}
          </button>
        </div>
      </div>

      {/* Confirm Modal (Cancel, Reactivate) */}
      {confirmModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setConfirmModal(null)}
        >
          <div
            className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-sm w-full p-5 sm:p-6 border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2">
              {confirmModal === 'cancel' ? t('billing.cancelConfirmTitle') : t('billing.reactivateConfirmTitle')}
            </h3>
            <p className="text-slate-600 text-sm mb-4 sm:mb-6">
              {confirmModal === 'cancel' ? t('billing.cancelConfirm') : t('billing.reactivateConfirm')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="flex-1 py-2.5 px-4 rounded-xl border-2 border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
              >
                {t('careerStatus.cancel')}
              </button>
              <button
                onClick={async () => {
                  const action = confirmModal;
                  setConfirmModal(null);
                  if (action === 'cancel') await handleCancel();
                  else await handleReactivate();
                }}
                className={`flex-1 py-2.5 px-4 rounded-xl font-bold transition-colors ${
                  confirmModal === 'cancel'
                    ? 'bg-slate-700 hover:bg-slate-800 text-white'
                    : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white'
                }`}
              >
                {confirmModal === 'cancel' ? t('billing.confirmCancel') : t('billing.confirmReactivate')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Success/Error Modal (Cancel, Reactivate) */}
      {actionModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setActionModal(null)}
        >
          <div
            className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-sm w-full p-5 sm:p-6 text-center border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            {actionModal.type === 'error' ? (
              <>
                <div className="mx-auto mb-3 sm:mb-4 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2">{t('common:error') || 'Error'}</h3>
                <p className="text-slate-600 text-sm mb-4 sm:mb-6">{actionModal.message}</p>
              </>
            ) : (
              <>
                <div className="mx-auto mb-3 sm:mb-4 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2">
                  {actionModal.type === 'cancel_success' ? t('billing.cancelSuccess') : t('billing.reactivateSuccess')}
                </h3>
                {actionModal.type === 'cancel_success' && (
                  <p className="text-slate-600 text-sm mb-4 sm:mb-6">{t('billing.cancelWarning')}</p>
                )}
              </>
            )}
            <button
              onClick={() => setActionModal(null)}
              className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-2.5 sm:py-3 px-4 sm:px-6 rounded-xl hover:from-pink-600 hover:to-rose-600 transition-all"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingSettings;

